import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  AddClientThunk,
  UpdateClientThunk,
  GetSingleClientThunk,
} from "../../redux/states/clients/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Upload,
  Save,
  ArrowLeft,
  Loader2,
  Trash2,
  User,
  Mail,
  Building,
  Phone,
  MapPin,
  Globe,
} from "lucide-react";

const CreateUpdateClients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleClient, loading, error } = useSelector(
    (state) => state.clients
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    company_name: "",
    gst_number: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    profile_image: null,
  });

  const [preview, setPreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleClientThunk(uuid));
    }
  }, [uuid, dispatch]);

  useEffect(() => {
    if (singleClient && isUpdating) {
      const profile = singleClient.profile_data || {};
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
        mobile: profile.mobile || "",
        whatsapp: profile.whatsapp || "",
        company_name: profile.company_name || "",
        gst_number: profile.gst_number || "",
        address: profile.address || "",
        city: profile.city || "",
        state: profile.state || "",
        country: profile.country || "",
        pincode: profile.pincode || "",
        profile_image: null,
      });
      if (profile.profile_image) {
        setPreview(`${process.env.REACT_APP_API_URL}/${profile.profile_image}`);
      }
    }
  }, [singleClient, isUpdating]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, profile_image: null }));
    setPreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== "") data.append(key, value);
    });

    if (isUpdating) data.append("uuid", uuid);

    const action = isUpdating
      ? UpdateClientThunk({ uuid, formData: data })
      : AddClientThunk(data);

    dispatch(action).then((result) => {
      if (result.type.endsWith("fulfilled")) {
        navigate("/clients");
      }
    });
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-sm font-bold text-gray-800">
                {isUpdating ? "Update Client" : "Add New Client"}
              </h1>
              <p className="text-sm text-gray-600 mt-2">
                {isUpdating
                  ? "Update client details and profile info"
                  : "Add a new client to your system"}
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors px-4 py-2 border border-gray-300 rounded-lg hover:border-blue-600"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-red-700">
              ⚠️ {error}
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6"
          >
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                icon={User}
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <InputField
                icon={Mail}
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              <InputField
                icon={Phone}
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
              />
              <InputField
                icon={Phone}
                label="WhatsApp"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
              />
              <InputField
                icon={Building}
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
              />
              <InputField
                icon={Building}
                label="GST Number"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
              />
              <InputField
                icon={MapPin}
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
              <InputField
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
              <InputField
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
              <InputField
                icon={Globe}
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
              <InputField
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
              />
            </div>

            {/* Profile Image */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Profile Image
              </h2>

              <div className="flex flex-col items-center space-y-4">
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Profile Preview"
                      className="w-40 h-40 object-cover rounded-full border-2 border-gray-300 shadow"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-full flex flex-col items-center justify-center bg-gray-50">
                    <Upload className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-sm text-gray-500">Upload Image</span>
                  </div>
                )}

                <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl cursor-pointer hover:bg-blue-700 transition">
                  <Upload className="w-5 h-5" />
                  {preview ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    name="profile_image"
                    onChange={handleChange}
                    className="hidden"
                    accept="image/*"
                  />
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Client" : "Save Client"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

/* -------------------- Reusable Input Field -------------------- */
const InputField = ({ icon: Icon, label, name, value, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex items-center border border-gray-300 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500">
      {Icon && <Icon className="w-4 h-4 text-gray-400 mr-2" />}
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full outline-none bg-transparent text-gray-900 text-base"
      />
    </div>
  </div>
);

export default CreateUpdateClients;
